import React, { useContext, useEffect, useState } from "react";
import * as R from "ramda";
import { connect } from "react-redux";
import {
  compose,
  withHandlers,
  lifecycle,
  withProps,
  withState
} from "recompose";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Badge,
  Tooltip,
  InputNumber,
  Icon,
  Button as ButtonAntd
} from "antd";
import { Content } from "@worcket/common/components/layout";
import { Switch } from "antd/lib";

import Toggle from "../../../../components/inputs/Toggle";
import CloneButton from "../../../../components/opportunity/CloneButton";
import ShareButton from "../../../../components/opportunity/ShareButton";
import {
  saveOpportunityDetails,
  SAVE_OPPORTUNITY_DETAILS
} from "../../../../store/actions/opportunity";
import { isLoading } from "../../../../utils/actions";
import {
  FORMS_KEYS,
  DEFAULT_PROCESS_STEPS
} from "../../../../constants/opportunity";
import Button from "../../../../components/Button";
import * as gtag from "../../../../lib/gtag";
import { Currencies } from "../../../../utils/currencies";

import api from "./../../../../utils/api";
import CurrencyInput from "./CurrencyInput";
import SwitchPublicData from "./SwitchPublicData";
import locales from "./../../../../locales";

import styled, { ThemeContext } from "styled-components/macro";

const { TextArea } = Input;

const Container = styled.div`
  position: relative;

  .form {
    .ant-form-item {
      padding-bottom: 0 !important;
    }

    .submit {
      margin: 0 !important;
      padding: 0 !important;
    }

    .ant-badge {
      width: 100% !important;
    }

    .green {
      .ant-badge-count {
        background: ${({ theme }) => theme["@primary"]} !important;
      }
    }
  }

  .top-actions {
    position: absolute;
    right: -5px;
    top: -5px;
    display: flex;
    align-items: center;
    gap: 15px;

    .buttons {
      button {
        margin-left: 5px;
        min-width: 140px;
        &:not(:last-child) {
          margin-right: 10px;
        }
      }
    }
  }

  .details-description {
    max-width: 750px;
    margin-bottom: 25px;
  }
`;

const WrapperNotes = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const TitleNotes = styled.span`
  font-size: 1.5em;
  font-weight: lighter;
  color: ${({ theme }) => theme["@gray-80"]};
`;

const RowNotes = styled(Row)`
  margin-left: -24px !important;
  margin-right: -24px !important;
  margin-bottom: 24px;
  padding: 24px;
  padding-bottom: 0px;
  background: ${({ theme }) => theme["@gray-20"]};
`;

const TextAreaCustom = styled(TextArea)`
  color: ${({ theme }) => theme["@gray-80"]} !important;
  background: ${({ canEditNotes, theme }) =>
    !canEditNotes && theme["@gray-20"]}!important;
  resize: none;
  border: ${({ canEditNotes }) => !canEditNotes && "0px"} !important;

  &.ant-input[disabled] {
    cursor: default !important;
  }
`;

const WrapperButton = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const SwitchButton = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid
    ${({ theme, checked }) => (checked ? theme["@primary"] : theme["@gray-40"])};
  border-radius: 30px;
  padding-inline: 7px;

  .ant-form-item {
    /* margin-bottom: 0; */
    margin-block: -3px;
  }

  .label {
    color: ${({ theme, checked }) =>
      checked ? theme["@primary"] : theme["@gray-60"]};
    font-size: 11px;
    margin-left: 10px;
  }

  .icon {
    margin-right: 2px;
    margin-left: 5px;
    color: #3d64ff;
  }
`;

const DetailsForm = ({
  t,
  // i18n,
  companyLanguage,
  form,
  teams,
  saving,
  isDisabled,
  opportunity,
  handleSubmit,
  newOpportunity,
  fetchingMatches,
  canEditNotes,
  setCanEditNotes,
  setForm
}) => {
  const theme = useContext(ThemeContext);
  // const companyT = i18n.getFixedT(companyLanguage);
  // const idCurrency = companyT("currency:id");
  // const labelCurrency = companyT("currency:label");
  const companyT = path => R.path(path.split(":"), locales[companyLanguage]);
  const idCurrency = opportunity.currencyId || companyT("currency:id");
  // const labelCurrency = companyT("currency:label");
  const { getFieldDecorator, getFieldValue } = form;
  const count = R.pipe(
    R.defaultTo(""),
    R.length,
    R.subtract(5000)
  )(getFieldValue("description"));

  const [notes, setNotes] = useState(
    opportunity.notes || getFieldValue("notes")
  );

  useEffect(() => {
    setForm && !newOpportunity && setForm(form.getFieldsValue());
  }, [form, newOpportunity, setForm]);

  return (
    <Content>
      <Container data-test="opportunity-details-form">
        <h2>{t("opportunity:details.title")}</h2>
        <div className="top-actions">
          {!newOpportunity && (
            <div className="buttons">
              <CloneButton opportunity={opportunity} />
              <ShareButton opportunity={opportunity} />
            </div>
          )}
          <SwitchButton checked={getFieldValue("private")}>
            <Form.Item>
              {getFieldDecorator("private", {
                initialValue: false,
                valuePropName: "checked"
              })(<Switch disabled={isDisabled} />)}
            </Form.Item>
            <span className="label">{t("opportunity:details.private")}</span>
            <Tooltip title={t("opportunity:details.tooltipPrivate")}>
              <Icon className="icon" type="question-circle" />
            </Tooltip>
          </SwitchButton>
        </div>
        <p className="details-description">
          {t("opportunity:details.detailsFormDescription1")}
          <br />
          {t("opportunity:details.detailsFormDescription2")}
        </p>

        <Form className="form" layout="vertical" onSubmit={handleSubmit}>
          <RowNotes gutter={20} type="flex" justify="center">
            <Col xs={24} md={24} style={{ padding: 0 }}>
              <Form.Item
                label={
                  <WrapperNotes>
                    <div>
                      <TitleNotes>{t("opportunity:details.notes")}</TitleNotes>
                      <Tooltip title={t("opportunity:details.tooltipNotes")}>
                        <Icon
                          style={{ marginLeft: 10, color: "#3D64FF" }}
                          type="question-circle"
                        />
                      </Tooltip>
                    </div>
                    {notes && (
                      <ButtonAntd
                        type={canEditNotes ? "primary" : "default"}
                        shape="circle"
                        icon="edit"
                        onClick={() => setCanEditNotes(!canEditNotes)}
                      />
                    )}
                  </WrapperNotes>
                }
              >
                {!notes ? (
                  <WrapperButton>
                    <Button
                      onClick={() => {
                        setNotes(true);
                        setCanEditNotes(true);
                        gtag.event({
                          category: "VacancyDetail",
                          action: "VacancyDetail.Note-Add",
                          label: opportunity.id
                        });
                      }}
                    >
                      {t("opportunity:details.createNote")}
                    </Button>
                  </WrapperButton>
                ) : (
                  <Badge
                    showZero
                    overflowCount={5000}
                    style={{
                      background: theme["@primary"],
                      display: canEditNotes ? "block" : "none"
                    }}
                    count={
                      5000 -
                      (getFieldValue("notes")
                        ? getFieldValue("notes").length
                        : 0)
                    }
                  >
                    {getFieldDecorator("notes")(
                      <TextAreaCustom
                        maxLength={5000}
                        canEditNotes={canEditNotes}
                        data-test="notes-input"
                        autoSize={{ minRows: 7, maxRows: 7 }}
                        disabled={isDisabled || !canEditNotes}
                      />
                    )}
                  </Badge>
                )}
              </Form.Item>
            </Col>
          </RowNotes>
          <Row gutter={20} type="flex" justify="center">
            <Col xs={24} md={12}>
              <Form.Item label={t("opportunity:details.name")}>
                {getFieldDecorator("title", {
                  rules: [
                    {
                      required: true,
                      message: t("form:emptyFieldValidation")
                    },
                    {
                      whitespace: true,
                      message: t("form:emptyFieldValidation")
                    },
                    {
                      max: 75,
                      message: t("form:maxCharsValidation", {
                        chars: 75
                      })
                    }
                  ]
                })(<Input disabled={isDisabled} data-test="name-input" />)}
              </Form.Item>

              {teams && teams.length !== 0 && (
                <Form.Item label={t("opportunity:details.team")}>
                  {getFieldDecorator("team")(
                    <Select
                      placeholder={t("opportunity:details.noTeam")}
                      disabled={isDisabled}
                      data-test="teams-input"
                      showSearch={true}
                      optionFilterProp="children"
                      allowClear={true}
                      clearIcon={
                        <Tooltip
                          placement="top"
                          title={t("opportunity:details.removeFromTeam")}
                        >
                          <Icon
                            type="close-circle"
                            style={{
                              fontSize: "18px",
                              position: "relative",
                              top: "-4px",
                              left: "-4px"
                            }}
                          />
                        </Tooltip>
                      }
                    >
                      {teams.map(item => (
                        <Select.Option key={item.id}>{item.name}</Select.Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
              )}
              <Form.Item label={t("opportunity:details.positions")}>
                {getFieldDecorator("positions", {
                  initialValue: 1,
                  rules: [
                    {
                      required: true,
                      message: t("form:positiveNumberValidation")
                    }
                  ]
                })(
                  <InputNumber
                    data-test="positions-input"
                    min={1}
                    max={1000}
                    disabled={isDisabled}
                  />
                )}
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={t("global:description")}
                className="description"
              >
                <Badge
                  count={count}
                  showZero
                  overflowCount={5000}
                  className={count < 0 ? "red" : "green"}
                >
                  {getFieldDecorator("description", {
                    rules: [
                      {
                        required: true,
                        message: t("form:minCharsValidation", { chars: 10 })
                      },
                      {
                        min: 10,
                        message: t("form:minCharsValidation", { chars: 10 })
                      },
                      {
                        max: 5000,
                        message: t("opportunity:details.descriptionMax", {
                          count: 5000
                        })
                      }
                    ]
                  })(
                    <TextArea
                      data-test="description-input"
                      autoSize={{ minRows: 5, maxRows: 5 }}
                      disabled={isDisabled}
                    />
                  )}
                </Badge>
              </Form.Item>

              <Form.Item label={t("opportunity:details.jobType")}>
                <div style={{ display: "flex" }}>
                  {getFieldDecorator("jobType")(
                    <Select disabled={isDisabled}>
                      <Select.Option key="fulltime">
                        {t("catalogs:jobTypes.fulltime")}
                      </Select.Option>
                      <Select.Option key="parttime">
                        {t("catalogs:jobTypes.parttime")}
                      </Select.Option>
                      <Select.Option key="hourly">
                        {t("catalogs:jobTypes.hourly")}
                      </Select.Option>
                      <Select.Option key="remote">
                        {t("catalogs:jobTypes.remote")}
                      </Select.Option>
                      <Select.Option key="internship">
                        {t("catalogs:jobTypes.internship")}
                      </Select.Option>
                      <Select.Option key="young">
                        {t("catalogs:jobTypes.young")}
                      </Select.Option>
                      <Select.Option key="eventual">
                        {t("catalogs:jobTypes.eventual")}
                      </Select.Option>
                      <Select.Option key="eventual-indefinite">
                        {t("catalogs:jobTypes.eventual-indefinite")}
                      </Select.Option>
                      <Select.Option key="undefined">
                        {t("catalogs:jobTypes.undefined")}
                      </Select.Option>
                      <Select.Option key="freelance">
                        {t("catalogs:jobTypes.freelance")}
                      </Select.Option>
                      <Select.Option key="others">
                        {t("catalogs:jobTypes.others")}
                      </Select.Option>
                    </Select>
                  )}
                  <SwitchPublicData
                    form={form}
                    field="showJobType"
                    disabled={isDisabled}
                  />
                </div>
              </Form.Item>
              <Form.Item label={t("opportunity:details.jobPayment")}>
                <div style={{ display: "flex" }}>
                  {getFieldDecorator("jobPayment")(
                    <Select disabled={isDisabled}>
                      <Select.Option key="effective">
                        {t("catalogs:jobPayment.effective")}
                      </Select.Option>
                      <Select.Option key="temporary">
                        {t("catalogs:jobPayment.temporary")}
                      </Select.Option>
                      <Select.Option key="atWill">
                        {t("catalogs:jobPayment.atWill")}
                      </Select.Option>
                    </Select>
                  )}
                  <SwitchPublicData
                    form={form}
                    field="showContractType"
                    disabled={isDisabled}
                  />
                </div>
              </Form.Item>

              <Form.Item>
                {getFieldDecorator("hasSalaryRange", {
                  valuePropName: "checked",
                  initialValue: !R.anyPass([
                    R.isNil,
                    R.isEmpty,
                    R.equals([null, null])
                  ])(opportunity.salaryRange)
                })(
                  <Toggle disabled={isDisabled}>
                    {({ toggle }) => (
                      <div>
                        <label style={{ fontWeight: 600 }}>
                          {t("opportunity:details.salaryRangePlaceholder")}{" "}
                          {toggle}
                        </label>
                        {getFieldValue("hasSalaryRange") ? (
                          <Form.Item
                            help={
                              ((!getFieldValue("salaryRange[0]") ||
                                !getFieldValue("salaryRange[1]")) &&
                                t("form:emptyFieldValidation")) ||
                              (getFieldValue("salaryRange[0]") >
                                getFieldValue("salaryRange[1]") &&
                                t("form:minMaxValidation"))
                            }
                            validateStatus={
                              ((!getFieldValue("salaryRange[0]") ||
                                !getFieldValue("salaryRange[1]") ||
                                getFieldValue("salaryRange[0]") >
                                  getFieldValue("salaryRange[1]")) &&
                                "error") ||
                              "success"
                            }
                          >
                            <Input.Group compact style={{ marginTop: 10 }}>
                              <Form.Item>
                                {getFieldDecorator("currencyId", {
                                  initialValue: idCurrency
                                })(
                                  <Select
                                    dropdownMatchSelectWidth={false}
                                    style={{
                                      "min-width": 60,
                                      width: "auto",
                                      zIndex: 1,
                                      cursor: "default",
                                      "text-align": "center"
                                    }}
                                  >
                                    {Currencies.map(item => (
                                      <Select.Option
                                        key={item.id}
                                        value={item.id}
                                      >
                                        {item.description}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                )}
                              </Form.Item>
                              {getFieldDecorator("salaryRange[0]")(
                                <CurrencyInput />
                              )}
                              <Input
                                style={{
                                  width: 55,
                                  zIndex: 1,
                                  cursor: "default",
                                  "text-align": "center"
                                }}
                                disabled
                                placeholder={getFieldValue("currencyId")}
                              />
                              {getFieldDecorator("salaryRange[1]")(
                                <CurrencyInput />
                              )}
                              <Input
                                style={{
                                  width: 55,
                                  zIndex: 1,
                                  cursor: "default",
                                  "text-align": "center"
                                }}
                                disabled
                                placeholder={getFieldValue("currencyId")}
                              />
                            </Input.Group>
                          </Form.Item>
                        ) : (
                          <div />
                        )}
                      </div>
                    )}
                  </Toggle>
                )}
              </Form.Item>
            </Col>
          </Row>

          {!isDisabled && newOpportunity && (
            <Row
              style={{ marginTop: "24px" }}
              type="flex"
              justify={newOpportunity ? "space-between" : "end"}
            >
              {newOpportunity && (
                <Link to="/oportunidades">
                  <Button size="lg" type="quaternary" disabled={saving}>
                    {t("global:cancel")}
                  </Button>
                </Link>
              )}
              <Form.Item className="submit">
                <Button
                  size="lg"
                  data-test="save-details-btn"
                  loading={saving || fetchingMatches}
                  type="primary"
                  htmlType="submit"
                >
                  {newOpportunity ? t("global:next") : t("global:save")}
                </Button>
              </Form.Item>
            </Row>
          )}
        </Form>
      </Container>
    </Content>
  );
};

const enhancer = compose(
  withTranslation(["opportunity", "form", "global"]),
  Form.create(),
  connect(
    R.applySpec({
      saving: state => isLoading(SAVE_OPPORTUNITY_DETAILS, state),
      newOpportunity: R.path(["opportunity", "newOpportunity"]),
      allTeams: R.propOr([], "teams"),
      sessionUser: R.pathOr([], ["user"]),
      companyLanguage: R.pathOr(null, ["company", "language"])
    }),
    {
      onSubmit: saveOpportunityDetails
    }
  ),
  lifecycle({
    componentDidMount() {
      const { form, opportunity, initialValues } = this.props;
      const values = R.pick(FORMS_KEYS.details, opportunity);

      const data =
        opportunity.team === null ? R.dissoc("team", values) : values;
      form.setFieldsValue(R.merge(data, initialValues));
    }
  }),
  withState("fetchingMatches", "setFetchingMatches", false),
  withState("canEditNotes", "setCanEditNotes", false),
  withHandlers({
    handleSubmit: ({
      form,
      onSubmit,
      newOpportunity,
      opportunity,
      setFetchingMatches,
      setCanEditNotes,
      t
    }) => event => {
      event.preventDefault();
      form.validateFields(async (err, values) => {
        if (!err) {
          if (
            values.hasSalaryRange &&
            (values.salaryRange.length !== 2 ||
              values.salaryRange[0] > values.salaryRange[1])
          )
            return;

          if (
            !values.hasSalaryRange ||
            !values.salaryRange ||
            R.equals(values.salaryRange, [undefined, undefined])
          ) {
            values.salaryRange = [];
          }

          delete values.hasSalaryRange;
          if (!values.team) values.team = null;
          setCanEditNotes(false);

          gtag.event({
            category: "VacancyDetail",
            action: "VacancyDetail.Detail-Save",
            label: opportunity.id
          });

          if (newOpportunity || opportunity.positions === values.positions) {
            return onSubmit({ id: opportunity.id, formData: values });
          }

          setFetchingMatches(true);
          const [matches, steps] = [
            await api.candidates.getMatches(
              opportunity.idCompany,
              opportunity.id
            ),
            await api.opportunity.process.fetch(
              opportunity.idCompany,
              opportunity.id
            )
          ];
          setFetchingMatches(false);

          const acceptedStep = R.find(
            R.propEq("name", DEFAULT_PROCESS_STEPS.accepted)
          )(steps);
          const acceptedCandidatesCount = R.filter(
            match => match.step === acceptedStep.id,
            matches || []
          ).length;

          if (values.positions < acceptedCandidatesCount) {
            form.setFields({
              positions: {
                value: values.positions,
                errors: [new Error(t("opportunity:details.errorPositions"))]
              }
            });
          } else {
            onSubmit({ id: opportunity.id, values });
          }
        }
      });
    }
  }),

  withProps(({ allTeams, opportunity, sessionUser }) => {
    if (
      sessionUser.roles.includes("admin") ||
      sessionUser.roles.includes("headadmin")
    ) {
      return { teams: allTeams };
    }

    const teamsOwnedByUser = R.filter(
      R.propEq("owner", sessionUser.id),
      allTeams
    );
    const teamsJoined = R.filter(
      R.propSatisfies(
        R.pipe(
          R.defaultTo([]),
          R.map(R.prop("id")),
          R.includes(sessionUser.id)
        ),
        "users"
      ),
      allTeams
    );

    const opportunityTeam =
      opportunity.team && R.find(R.propEq("id", opportunity.team))(allTeams);
    return {
      teams: R.uniqBy(R.prop("id"), [
        ...teamsOwnedByUser,
        ...teamsJoined,
        ...(opportunityTeam ? [opportunityTeam] : [])
      ])
    };
  })
);

export default enhancer(DetailsForm);
